import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useState,
} from 'react';

// biome-ignore lint/suspicious/noExplicitAny: table rows, api handles, and ref payloads are dynamic legacy shapes
type AnyData = any;

interface ContractSummaryData {
  summary: string;
  contractName: string;
  contractId: string;
}

/**
 * Shared UI state for the AG-grid pages (contracts, clauses, users):
 * the grid api handle + active row, plus the open/close flags for every
 * row-action dialog. Payload-bearing fields are `AnyData` (dynamic mongoose
 * docs / grid apis); flags and setters are precisely typed.
 */
interface AGTableContextValue {
  tableApi: AnyData;
  setTableApi: Dispatch<SetStateAction<AnyData>>;
  tableData: AnyData;
  setTableData: Dispatch<SetStateAction<AnyData>>;
  activeRow: number;
  setActiveRow: Dispatch<SetStateAction<number>>;
  activeUserData: AnyData;
  setActiveUserData: Dispatch<SetStateAction<AnyData>>;
  update: number;
  setUpdate: Dispatch<SetStateAction<number>>;
  isActiveTab: boolean;
  setIsActiveTab: Dispatch<SetStateAction<boolean>>;
  openDelete: boolean;
  setOpenDelete: Dispatch<SetStateAction<boolean>>;
  openMultiDelete: boolean;
  setOpenMultiDelete: Dispatch<SetStateAction<boolean>>;
  openNewContract: boolean;
  setOpenNewContract: Dispatch<SetStateAction<boolean>>;
  openEdit: boolean;
  setOpenEdit: Dispatch<SetStateAction<boolean>>;
  openArchive: boolean;
  setOpenArchive: Dispatch<SetStateAction<boolean>>;
  openMultiArchive: boolean;
  setOpenMultiArchive: Dispatch<SetStateAction<boolean>>;
  openEditUser: boolean;
  setOpenEditUser: Dispatch<SetStateAction<boolean>>;
  openNewGroup: boolean;
  setOpenNewGroup: Dispatch<SetStateAction<boolean>>;
  openClause: boolean;
  setOpenClause: Dispatch<SetStateAction<boolean>>;
  openNewClause: boolean;
  setOpenNewClause: Dispatch<SetStateAction<boolean>>;
  openDownload: boolean;
  setOpenDownload: Dispatch<SetStateAction<boolean>>;
  dataUtility: AnyData;
  setDataUtility: Dispatch<SetStateAction<AnyData>>;
  openContractLog: boolean;
  setOpenContractLog: Dispatch<SetStateAction<boolean>>;
  openContractReview: boolean;
  setOpenContractReview: Dispatch<SetStateAction<boolean>>;
  openContractSummary: boolean;
  setOpenContractSummary: Dispatch<SetStateAction<boolean>>;
  contractSummaryData: ContractSummaryData;
  setContractSummaryData: Dispatch<SetStateAction<ContractSummaryData>>;
}

const AGTableContext = createContext<AGTableContextValue | undefined>(undefined);

/** Consumers assert non-undefined — every consumer renders under <AGTableProvider>. */
export const useAGTableContext = (): AGTableContextValue =>
  useContext(AGTableContext) as AGTableContextValue;

export function AGTableProvider({ children }: { children: ReactNode }) {
  const [tableApi, setTableApi] = useState<AnyData>(null);
  const [tableData, setTableData] = useState<AnyData>(null);
  const [activeRow, setActiveRow] = useState(-1);
  const [activeUserData, setActiveUserData] = useState<AnyData>(null);
  const [update, setUpdate] = useState(0);
  const [isActiveTab, setIsActiveTab] = useState(true);
  const [openDelete, setOpenDelete] = useState(false);
  const [openMultiDelete, setOpenMultiDelete] = useState(false);
  const [openNewContract, setOpenNewContract] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openArchive, setOpenArchive] = useState(false);
  const [openMultiArchive, setOpenMultiArchive] = useState(false);
  const [openEditUser, setOpenEditUser] = useState(false);
  const [openNewGroup, setOpenNewGroup] = useState(false);
  const [openClause, setOpenClause] = useState(false);
  const [openNewClause, setOpenNewClause] = useState(false);
  const [openDownload, setOpenDownload] = useState(false);
  const [openContractLog, setOpenContractLog] = useState(false);
  const [openContractReview, setOpenContractReview] = useState(false);
  const [openContractSummary, setOpenContractSummary] = useState(false);
  const [contractSummaryData, setContractSummaryData] = useState<ContractSummaryData>({
    summary: '',
    contractName: '',
    contractId: '',
  });
  const [dataUtility, setDataUtility] = useState<AnyData>(null);

  return (
    <AGTableContext.Provider
      value={{
        tableApi,
        setTableApi,
        tableData,
        setTableData,
        activeRow,
        setActiveRow,
        activeUserData,
        setActiveUserData,
        update,
        setUpdate,
        isActiveTab,
        setIsActiveTab,
        openDelete,
        setOpenDelete,
        openMultiDelete,
        setOpenMultiDelete,
        openNewContract,
        setOpenNewContract,
        openEdit,
        setOpenEdit,
        openArchive,
        setOpenArchive,
        openMultiArchive,
        setOpenMultiArchive,
        openEditUser,
        setOpenEditUser,
        openNewGroup,
        setOpenNewGroup,
        openClause,
        setOpenClause,
        openNewClause,
        setOpenNewClause,
        openDownload,
        setOpenDownload,
        dataUtility,
        setDataUtility,
        openContractLog,
        setOpenContractLog,
        openContractReview,
        setOpenContractReview,
        openContractSummary,
        setOpenContractSummary,
        contractSummaryData,
        setContractSummaryData,
      }}
    >
      {children}
    </AGTableContext.Provider>
  );
}
